import { useRef, useState } from "react";

import { t } from "../../../i18n";
import { resolvePayerSchema, uploadInvoiceSchema } from "../../../lib/schemas";
import { getApiBase, trpc } from "../../../lib/trpc";
import type { ValidationIssue } from "../../../types/domain";
import { Button, Dialog, Field, Icon, Select, Stack, TapTarget } from "../../ui";
import type { Member, ResolvePayerState } from "../types";

interface UploadInvoiceDialogProps {
  settlementId: string;
  members: Member[];
  open: boolean;
  onClose: () => void;
}

export function UploadInvoiceDialog({
  settlementId,
  members,
  open,
  onClose,
}: UploadInvoiceDialogProps) {
  const utils = trpc.useUtils();
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  // Long-press paste-state: starter timer pa touchstart, kanselleres pa touchend
  // hvis brukeren slipper for tiden gar ut. Triggered-flagget brukes til a
  // forhindre at tap-then-release ogsa apner filvelgeren.
  const longPressTimer = useRef<number | null>(null);
  const longPressTriggered = useRef(false);
  // When the upload returns unmatched cardholder names, switch to a resolve
  // step where the user picks one household member to cover those expenses.
  const [resolve, setResolve] = useState<ResolvePayerState | null>(null);
  const [resolvePayerId, setResolvePayerId] = useState<string>("");
  const [resolveSubmitting, setResolveSubmitting] = useState(false);
  // Warning-severity issues from the extraction (sum mismatch etc.) that the
  // user should acknowledge before the invoice is considered reviewed.
  const [issues, setIssues] = useState<ValidationIssue[]>([]);

  const bulkAssignPayer = trpc.settlement.bulkAssignPayer.useMutation();

  function reset() {
    setFile(null);
    setError(null);
    setUploading(false);
    setResolve(null);
    setResolvePayerId("");
    setResolveSubmitting(false);
    setIssues([]);
  }

  async function upload() {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append("pdf", file);
      const url = `${getApiBase()}/upload/invoice?settlementId=${encodeURIComponent(settlementId)}`;
      const res = await fetch(url, {
        method: "POST",
        body: formData,
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      const body = (await res.json().catch(() => ({}))) as {
        unresolvedExpenseIds?: string[];
        unmatchedNames?: string[];
        issues?: ValidationIssue[];
      };
      // Always refresh so extracted expenses appear (the resolve step edits them in place).
      utils.settlement.get.invalidate({ settlementId });
      const warnings = (body.issues ?? []).filter(
        (i) =>
          i.severity === "warning" &&
          (i.code === "sum_mismatch" || i.code === "total_amount_disagreement"),
      );
      setIssues(warnings);
      const unresolved = body.unresolvedExpenseIds ?? [];
      if (unresolved.length > 0) {
        setResolve({
          expenseIds: unresolved,
          unmatchedNames: body.unmatchedNames ?? [],
        });
        setResolvePayerId(members[0]?.userId ?? "");
        setUploading(false);
        return;
      }
      if (warnings.length > 0) {
        // No payer resolution needed, but the user still has to acknowledge
        // extraction warnings before we close the dialog.
        setUploading(false);
        return;
      }
      reset();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setUploading(false);
    }
  }

  async function submitResolve() {
    if (!resolve) return;
    const parsed = resolvePayerSchema.safeParse({ paidBy: resolvePayerId });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? t.validation.selectMember);
      return;
    }
    setResolveSubmitting(true);
    setError(null);
    try {
      await bulkAssignPayer.mutateAsync({
        expenseIds: resolve.expenseIds,
        paidByUserId: parsed.data.paidBy,
      });
      await utils.settlement.get.invalidate({ settlementId });
      reset();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setResolveSubmitting(false);
    }
  }

  function handleFileChange(selected: File | null) {
    if (!selected) {
      setFile(null);
      return;
    }
    const result = uploadInvoiceSchema.safeParse({ file: selected });
    if (!result.success) {
      setError(result.error.issues[0]?.message ?? t.validation.fileRequired);
      return;
    }
    setError(null);
    setFile(selected);
  }

  // Leser forste PDF i utklippstavlen via Clipboard API. Krever bruker-gesture
  // (touchend/click) for tilgang — derfor bare kalles fra long-press / tap-fall.
  async function pasteFromClipboard() {
    if (!navigator.clipboard?.read) {
      setError(t.settlement.invoices.uploadDialog.pasteFailed);
      return;
    }
    try {
      const items = await navigator.clipboard.read();
      for (const item of items) {
        const pdfType = item.types.find((type) => type === "application/pdf");
        if (!pdfType) continue;
        const blob = await item.getType(pdfType);
        const pasted = new File([blob], "utklippstavle.pdf", { type: pdfType });
        handleFileChange(pasted);
        return;
      }
      setError(t.settlement.invoices.uploadDialog.pasteNoPdf);
    } catch {
      setError(t.settlement.invoices.uploadDialog.pasteFailed);
    }
  }

  const startLongPress = () => {
    longPressTriggered.current = false;
    longPressTimer.current = window.setTimeout(() => {
      longPressTriggered.current = true;
      pasteFromClipboard();
    }, 500);
  };

  const cancelLongPress = () => {
    if (longPressTimer.current !== null) {
      window.clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const issuesNotice =
    issues.length > 0 ? (
      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/30">
        <div className="flex items-start gap-2">
          <Icon
            name="alert-circle"
            size="sm"
            className="mt-0.5 text-amber-600 dark:text-amber-300"
          />
          <div className="flex-1 space-y-2">
            {issues.map((issue, idx) => (
              <div key={`${issue.code}-${idx}`}>
                <p className="text-amber-900 text-sm dark:text-amber-200">
                  {issue.code === "sum_mismatch"
                    ? t.settlement.invoices.issuesDialog.sumMismatch
                    : issue.code === "total_amount_disagreement"
                      ? t.settlement.invoices.issuesDialog.totalAmountDisagreement
                      : t.settlement.invoices.issuesDialog.fallback}
                </p>
                <details className="mt-1 text-amber-800/80 text-xs dark:text-amber-200/70">
                  <summary className="cursor-pointer">
                    {t.settlement.invoices.issuesDialog.detailsLabel}
                  </summary>
                  <p className="mt-1 break-words font-mono">{issue.message}</p>
                </details>
              </div>
            ))}
          </div>
        </div>
      </div>
    ) : null;

  if (resolve) {
    const noMembers = members.length === 0;
    return (
      <Dialog
        open={open}
        onClose={() => {
          reset();
          onClose();
        }}
        title={t.settlement.invoices.resolvePayerDialog.title}
        description={t.settlement.invoices.resolvePayerDialog.description(
          resolve.expenseIds.length,
          resolve.unmatchedNames,
        )}
        footer={
          <>
            <Button
              onClick={() => {
                reset();
                onClose();
              }}
              disabled={resolveSubmitting}
            >
              {t.settlement.invoices.resolvePayerDialog.skip}
            </Button>
            <Button
              variant="primary"
              disabled={noMembers || !resolvePayerId || resolveSubmitting}
              isLoading={resolveSubmitting}
              onClick={submitResolve}
            >
              {t.settlement.invoices.resolvePayerDialog.submit}
            </Button>
          </>
        }
      >
        <Stack gap={3}>
          {issuesNotice}
          {noMembers ? (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/30">
              <Icon
                name="alert-circle"
                size="sm"
                className="mt-0.5 text-amber-600 dark:text-amber-300"
              />
              <span className="text-amber-900 text-sm dark:text-amber-200">
                {t.settlement.invoices.resolvePayerDialog.errorNoMembers}
              </span>
            </div>
          ) : (
            <Field label={t.settlement.invoices.resolvePayerDialog.paidByLabel}>
              <Select
                value={resolvePayerId}
                onChange={(e) => setResolvePayerId(e.target.value)}
                disabled={resolveSubmitting}
              >
                {members.map((m) => (
                  <option key={m.userId} value={m.userId}>
                    {m.displayName ?? m.email}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          {error && (
            <div className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 p-3 dark:border-rose-800 dark:bg-rose-900/30">
              <Icon
                name="alert-circle"
                size="sm"
                className="mt-0.5 text-rose-600 dark:text-rose-300"
              />
              <span className="text-rose-900 text-sm dark:text-rose-200">{error}</span>
            </div>
          )}
        </Stack>
      </Dialog>
    );
  }

  if (issues.length > 0) {
    return (
      <Dialog
        open={open}
        onClose={() => {
          reset();
          onClose();
        }}
        title={t.settlement.invoices.issuesDialog.title}
        description={t.settlement.invoices.issuesDialog.description}
        footer={
          <Button
            variant="primary"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            {t.settlement.invoices.issuesDialog.acknowledge}
          </Button>
        }
      >
        <Stack gap={3}>{issuesNotice}</Stack>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title={t.settlement.invoices.uploadDialog.title}
      description={t.settlement.invoices.uploadDialog.description}
      footer={
        <>
          <Button
            onClick={() => {
              reset();
              onClose();
            }}
            disabled={uploading}
          >
            {t.common.cancel}
          </Button>
          <Button
            variant="primary"
            disabled={!file || uploading}
            isLoading={uploading}
            onClick={upload}
          >
            {uploading
              ? t.settlement.invoices.uploadDialog.submitProcessing
              : t.settlement.invoices.uploadDialog.submitIdle}
          </Button>
        </>
      }
    >
      <Stack gap={3}>
        {/* biome-ignore lint/a11y/useKeyWithClickEvents: <label> has native
              tastatur-tilgjengelighet via det indre file-input — Enter pa
              fokusert label apner filvelgeren. onClick her resetter bare
              long-press-flagget og er irrelevant for tastatur. */}
        <label
          className={`relative block cursor-pointer select-none rounded-lg border-2 border-dashed p-5 text-center transition-colors sm:p-8 ${
            dragActive
              ? "border-accent-500 bg-accent-50 dark:bg-accent-900/30"
              : "border-ink-200 hover:border-ink-300 hover:bg-ink-50"
          }`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={() => setDragActive(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragActive(false);
            const dropped = e.dataTransfer.files[0];
            if (dropped) handleFileChange(dropped);
          }}
          onTouchStart={startLongPress}
          onTouchEnd={cancelLongPress}
          onTouchCancel={cancelLongPress}
          onTouchMove={cancelLongPress}
          onContextMenu={(e) => e.preventDefault()}
          onClick={(e) => {
            // Long-press har allerede limet inn — hindre at samme touch ogsa
            // apner filvelgeren.
            if (longPressTriggered.current) {
              e.preventDefault();
              longPressTriggered.current = false;
            }
          }}
        >
          {file ? (
            <Stack gap={2} className="items-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-accent-700 dark:bg-accent-900/40 dark:text-accent-300">
                <Icon name="file-text" size="md" />
              </div>
              <div className="font-medium text-ink-900 text-sm">{file.name}</div>
              <div className="text-ink-500 text-xs">{(file.size / 1024 / 1024).toFixed(2)} MB</div>
              <TapTarget
                className="text-accent-600 text-xs hover:underline dark:text-accent-400"
                onClick={(e) => {
                  e.preventDefault();
                  setFile(null);
                }}
              >
                {t.settlement.invoices.uploadDialog.chooseAnother}
              </TapTarget>
            </Stack>
          ) : (
            <>
              <Icon name="upload" size="lg" className="mx-auto mb-2 text-ink-400" />
              <p className="text-ink-700 text-sm">{t.settlement.invoices.uploadDialog.dropHere}</p>
              <p className="mt-1 text-ink-500 text-xs">
                {t.settlement.invoices.uploadDialog.clickToSelect}
              </p>
              <p className="mt-1 text-ink-400 text-xs sm:hidden">
                {t.settlement.invoices.uploadDialog.longPressHint}
              </p>
            </>
          )}
          <input
            type="file"
            accept="application/pdf"
            className="absolute inset-0 cursor-pointer opacity-0"
            onChange={(e) => handleFileChange(e.target.files?.[0] ?? null)}
            disabled={uploading}
          />
        </label>

        {error && (
          <div className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 p-3 dark:border-rose-800 dark:bg-rose-900/30">
            <Icon
              name="alert-circle"
              size="sm"
              className="mt-0.5 text-rose-600 dark:text-rose-300"
            />
            <span className="text-rose-900 text-sm dark:text-rose-200">{error}</span>
          </div>
        )}
      </Stack>
    </Dialog>
  );
}
